import { Activity, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function CompliancePage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="compliance-monitoring"
        icon={Activity}
        title="Compliance Monitoring Coming Soon"
        description="Real-time compliance tracking, gap analysis, and automated reporting for DORA, ISO 27001, NIST, and other regulatory frameworks will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Compliance Dashboard</h1>
          <p className="text-neutral-400 mt-1">
            Monitor compliance across regulatory frameworks
          </p>
        </div>
        <button className="btn-primary">
          Run Assessment
        </button>
      </div>

      {/* Framework Compliance Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">DORA</h3>
            <Activity className="text-primary-400" size={20} />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-success-500">87%</span>
            <span className="text-neutral-400 ml-2">compliant</span>
          </div>
          <div className="mt-4 h-2 bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary-500 to-cyan-500" style={{ width: '87%' }} />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">ISO 27001</h3>
            <CheckCircle className="text-success-500" size={20} />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-success-500">92%</span>
            <span className="text-neutral-400 ml-2">compliant</span>
          </div>
          <div className="mt-4 h-2 bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary-500 to-cyan-500" style={{ width: '92%' }} />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">NIST CSF</h3>
            <AlertTriangle className="text-warning-500" size={20} />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-warning-500">74%</span>
            <span className="text-neutral-400 ml-2">compliant</span>
          </div>
          <div className="mt-4 h-2 bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary-500 to-cyan-500" style={{ width: '74%' }} />
          </div>
        </Card>
      </div>

      {/* Compliance Requirements Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Compliance Activities</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Framework</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Requirement</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Control</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Last Review</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Next Due</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">DORA</td>
                <td className="py-3 px-4 text-sm text-neutral-300">ICT Risk Management</td>
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-089</td>
                <td className="py-3 px-4"><span className="badge-success">Compliant</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-15</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2025-01-15</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">ISO 27001</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Access Control Policy</td>
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-012</td>
                <td className="py-3 px-4"><span className="badge-success">Compliant</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-28</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2025-01-28</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">NIST CSF</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Incident Response Plan</td>
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-045</td>
                <td className="py-3 px-4"><span className="badge-warning">Gap Identified</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-09-12</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-15</td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">GDPR</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Data Protection Impact Assessment</td>
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-156</td>
                <td className="py-3 px-4"><span className="badge-success">Compliant</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-20</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2025-04-20</td>
              </tr>
              <tr className="hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Basel III</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Capital Adequacy Reporting</td>
                <td className="py-3 px-4 text-sm font-mono text-primary-400">CTRL-203</td>
                <td className="py-3 px-4"><span className="badge-primary">In Progress</span></td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-01</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-12-31</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
